import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { REQUEST_DOC_FLOWS_ENTITY } from '../../../constants/entity'
import { PROFILE_ROUTE_NAMES } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useInitData,
  useLinkLikeClick,
  useRegisterReload,
} from '../../../hooks'
import { hasDocFlowsSectionCreatePermission } from '../../../utils/docFlow'
import { DocFlowTableRow } from '../../DocFlowTableRow'
import { FilterDivider } from '../../FilterDivider'
import { PageActions } from '../../Page'
import { PageEmptyState } from '../../PageEmptyState'
import { Pagination } from '../../Pagination'
import { SelectionToolbar } from '../../SelectionToolbar'
import {
  getTableResetSortingButton,
  Table,
  TableConfigModal,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
  useTableRefresh,
} from '../../Table'
import { TablePagination } from '../../TablePagination'
import {
  useRequestProfileContext,
  useRequestProfileDocFlowLinkActions,
  useRequestProfileTabPerformingAction,
} from '../RequestProfile.hooks'

import { useRequestProfileTabDocFlowsTable } from './RequestProfileTabDocFlows.hooks'
import {
  getRequestProfileTabDocFlowsActions,
  getRequestProfileTabDocFlowsEmptyStateActions,
  hasRequestProfileTabDocFlowsItems,
} from './RequestProfileTabDocFlows.utils'
import { RequestProfileTabDocFlowsToolbar } from './RequestProfileTabDocFlowsToolbar'

import * as styles from './RequestProfileTabDocFlows.module.css'

import DocFlowIcon from '../../../icons/doc-splitting.svg'

function RequestProfileTabDocFlows() {
  const { t } = useTranslation()

  const { isPreview, registerDocFlowsTabReload, requestData } =
    useRequestProfileContext()

  const { sections } = useInitData()

  const pageScrollWrapperRef = useRef(null)

  const requestId = requestData?.id

  const canCreateDocFlow = hasDocFlowsSectionCreatePermission(sections)

  const {
    availableFilters,
    checkIsItemSelected,
    columns,
    filterValues,
    handleApplyConfig,
    handleChangeSearch,
    handleCleanSelection,
    handleClearSearch,
    handleConfigOpen,
    handleDensityChange,
    handleLoadInit,
    handleReload,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSetColumns,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    hasFilterChanged,
    isAllSelected,
    isConfigOpen,
    isDense,
    isInitiated,
    isLoading,
    isRefetching,
    isRemoveSelection,
    isSearchActive,
    items: docFlows,
    ordering,
    pagination,
    requiredColumns,
    search,
    selected,
    selectedIds,
    setConfigOpen,
  } = useRequestProfileTabDocFlowsTable({
    containerRef: pageScrollWrapperRef,
    isPreview,
    requestId,
  })

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  const {
    handleClickConnectDocFlow,
    handleClickCreateDocFlow,
    handleClickDisconnectDocFlows,
    isPerformingAction,
  } = useRequestProfileDocFlowLinkActions()

  useRequestProfileTabPerformingAction(isPerformingAction || isRefetching)

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  useEffect(() => {
    handleLoadInit()
  }, [])

  useRegisterReload({
    reload: handleReload,
    onRegisterReload: registerDocFlowsTabReload,
  })

  function handleClickRow(item, event) {
    handleClick({ event, link: PROFILE_ROUTE_NAMES.docFlow(item.id) })
  }

  function handleMouseDownRow(item, event) {
    handleMouseDown({ event, link: PROFILE_ROUTE_NAMES.docFlow(item.id) })
  }

  function handleDisconnectSelected() {
    handleClickDisconnectDocFlows({
      docFlowIds: selectedIds,
      onSuccess: handleCleanSelection,
    })
  }

  const massActions = getRequestProfileTabDocFlowsActions(t)

  function getItemActions() {
    return massActions
  }

  function handleCallItemAction(action, item) {
    handleClickDisconnectDocFlows({
      docFlowIds: [item.id],
      onSuccess: handleCleanSelection,
    })
  }

  const hasDocFlows = hasRequestProfileTabDocFlowsItems(docFlows)

  const shouldCollapseToolbar =
    !hasDocFlows && !hasFilterChanged && !isSearchActive && !isLoading

  const emptyStateActions = getRequestProfileTabDocFlowsEmptyStateActions({
    canCreateDocFlow,
    isLoading,
    shouldShowActions: shouldCollapseToolbar,
    t,
    onClickConnect: handleClickConnectDocFlow,
    onClickCreate: handleClickCreateDocFlow,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div
        className={styles.RequestProfileTabDocFlows}
        ref={pageScrollWrapperRef}
      >
        {shouldCollapseToolbar ? null : (
          <RequestProfileTabDocFlowsToolbar
            availableFilters={availableFilters}
            canCreateDocFlow={canCreateDocFlow}
            filterValues={filterValues}
            isSkeleton={isLoading}
            ordering={ordering}
            pagination={pagination}
            requestId={requestId}
            search={search}
            setFilterValues={handleSetFilterValues}
            onChangeSearch={handleChangeSearch}
            onClearSearch={handleClearSearch}
            onClickConnect={handleClickConnectDocFlow}
            onClickCreate={handleClickCreateDocFlow}
            onReload={handleReload}
          />
        )}
        {hasDocFlows || isLoading ? (
          <>
            <TableControlBar>
              <TableMassAction
                responsive
                actions={massActions}
                isAllSelected={isAllSelected}
                isLoading={!isInitiated}
                isRemoveSelection={isRemoveSelection}
                selectedSize={selected.length}
                setMassAction={handleDisconnectSelected}
                onSelectAll={handleSelectAll}
              />
              <PageActions isLoading={isLoading}>
                <TableControlButtons
                  ResetSortingButton={getTableResetSortingButton(ordering)}
                  configButtonProps={{ onClick: handleConfigOpen }}
                  densityButtonProps={{
                    isDense,
                    onClick: handleDensityChange,
                  }}
                  disabled={isLoading}
                  refreshButtonProps={refreshButtonProps}
                  resetSortingProps={{ onClick: handleResetOrdering }}
                />
                <FilterDivider />
                <Pagination
                  responsive
                  disabled={isLoading}
                  value={pagination}
                  onChange={handleSetPagination}
                  onChangeSize={handleSetSize}
                />
              </PageActions>
            </TableControlBar>
            <Table
              TableRow={DocFlowTableRow}
              checkIsItemSelected={checkIsItemSelected}
              columns={columns}
              containerRef={pageScrollWrapperRef}
              headerRowProps={{
                ordering,
                onApply: handleApplyConfig,
                onSetColumns: handleSetColumns,
                onSetOrdering: handleSetOrdering,
              }}
              isLoading={isLoading}
              items={docFlows?.items}
              rowProps={{
                getActions: getItemActions,
                isShort: isDense,
                selected,
                onCallAction: handleCallItemAction,
                onClick: handleClickRow,
                onMouseDown: handleMouseDownRow,
                onSelect: handleSelect,
              }}
            />
          </>
        ) : (
          <PageEmptyState
            EntityIcon={DocFlowIcon}
            actions={emptyStateActions}
            text={t('RequestProfile.DocFlowsEmptyStateKey')}
          />
        )}
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
        {selected.length > 0 ? (
          <SelectionToolbar
            selected={selected.length}
            onClose={handleCleanSelection}
          />
        ) : null}
        {isConfigOpen ? (
          <TableConfigModal
            columns={columns}
            entity={REQUEST_DOC_FLOWS_ENTITY}
            requiredColumns={requiredColumns}
            setColumns={handleSetColumns}
            setConfigOpen={setConfigOpen}
            onApply={handleApplyConfig}
          />
        ) : null}
      </div>
    </ScreenContainerRef>
  )
}

export default RequestProfileTabDocFlows
